class Panel extends HTMLElement {
  constructor() {
    super();

    this.isOpen = this.getAttribute('default-open') === 'true';
    this.alignment = this.getAttribute('alignment') || 'right';
    this.additionalClasses = this.getAttribute('additionalClasses') || '';
    this.context = this.getAttribute('context');

    this.updatePanelClass();

    this.handleOpenEvent = this.handleOpenEvent.bind(this);
    this.handleCloseEvent = this.handleCloseEvent.bind(this);
    this.handleToggleEvent = this.handleToggleEvent.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
  }

  slidePanelOpen() {
    document.querySelectorAll('[slidePanelOpen]').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelector('html').classList.add('overflow-hidden');
        document.querySelector('body').classList.add('overflow-hidden');
        document.querySelector('body').classList.add('no-scroll');
        document.querySelector('.main-product')?.classList.add('z-20');
        const triggerID = btn.getAttribute('slidePanelOpen');
        const slidePanel = document.querySelector(`slide-panel[slidePanelID="${triggerID}"]`);
        slidePanel.handleOpenEvent();
      });
    });
  }

  slidePanelClose() {
    document.querySelectorAll('[slidePanelClose]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('slide-panel');
        if (panel) {
          panel.handleCloseEvent();
        }
      });
    });
  }

  connectedCallback() {
    this.slidePanelOpen();
    this.slidePanelClose();

    this.triggers = {
      openTriggerEl: document.querySelector(this.getAttribute('openTriggerSelector')),
      closeTriggerEl: document.querySelector(this.getAttribute('closeTriggerSelector')),
      toggleEl: document.querySelector(this.getAttribute('toggleSelector')),
    };

    const { openTriggerEl, closeTriggerEl, toggleEl } = this.triggers;

    if (openTriggerEl) openTriggerEl.addEventListener('click', this.handleOpenEvent);
    if (closeTriggerEl) closeTriggerEl.addEventListener('click', this.handleCloseEvent);
    if (toggleEl) toggleEl.addEventListener('click', this.handleToggleEvent);
    if (this.context) {
      document.addEventListener(`${this.context}:open`, this.handleOpenEvent);
      document.addEventListener(`${this.context}:close`, this.handleCloseEvent);
    }

    this.initElements();
  }

  initElements() {
    this.panelForm = this.querySelector('.panel-content__form') || null;
  }

  setupFocusTrap() {
    // Get all focusable elements within the panel
    this.focusableElements = Array.from(
      this.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => {
      // Filter out hidden elements
      return el.offsetParent !== null;
    });

    if (this.focusableElements.length === 0) return;

    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

    // Focus the first element
    setTimeout(() => {
      this.firstFocusableElement.focus();
    }, 100);

    // Add keydown listener
    document.addEventListener('keydown', this.handleKeyDown);
  }

  removeFocusTrap() {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
  }

  handleKeyDown(e) {
    if (!this.isOpen) return;

    // Handle Tab key
    if (e.key === 'Tab') {
      // If no focusable elements, prevent default
      if (this.focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      // Shift + Tab (backward)
      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement.focus();
        }
      }
    }

    // Handle Escape key to close panel
    if (e.key === 'Escape') {
      this.handleCloseEvent();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'default-open') {
      this.isOpen = newValue === 'true';
      this.updatePanelClass();
    }
  }

  disconnectedCallback() {
    const { openTriggerEl, closeTriggerEl, toggleEl } = this.triggers;

    if (openTriggerEl) openTriggerEl.removeEventListener('click', this.handleOpenEvent);
    if (closeTriggerEl) closeTriggerEl.removeEventListener('click', this.handleCloseEvent);
    if (toggleEl) toggleEl.removeEventListener('click', this.handleToggleEvent);

    if (this.context) {
      document.removeEventListener(`${this.context}:open`, this.handleOpenEvent);
      document.removeEventListener(`${this.context}:close`, this.handleCloseEvent);
    }

    this.removeFocusTrap();
  }

  handleOpenEvent() {
    this.isOpen = true;
    this.updatePanelClass();
    this.setupFocusTrap();
  }

  handleCloseEvent() {
    this.isOpen = false;
    document.querySelector('html').classList.remove('overflow-hidden');
    document.querySelector('body').classList.remove('overflow-hidden');
    document.querySelector('body').classList.remove('no-scroll');
    document.querySelector('.main-product')?.classList.remove('z-20');
    this.updatePanelClass();
    this.removeFocusTrap();
  }

  handleToggleEvent() {
    this.isOpen = !this.isOpen;
    this.updatePanelClass();

    if (this.isOpen) {
      this.setupFocusTrap();
    } else {
      this.removeFocusTrap();
    }
  }

  static get observedAttributes() {
    return ['default-open'];
  }

  updatePanelClass() {
    const baseClass = 'block w-full h-full fixed top-0 bottom-0 z-40 bg-white';
    const alignmentClass = this.alignment === 'popup' ? 'right-0 left-0 scale-0' : `${this.alignment}-0 max-w-panel`;
    const closedClass = this.isOpen
      ? 'translate-x-0 block active visible'
      : this.alignment === 'left'
        ? '-translate-x-full invisible'
        : 'translate-x-full invisible';
    this.className = `${baseClass} ${alignmentClass} ${closedClass} ${this.additionalClasses}`;
  }
}

if (!customElements.get('slide-panel')) {
  customElements.define('slide-panel', Panel);
}
